/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as React from 'react';
import classname from 'classnames';
import get from 'lodash/get';
import { computed, toJS } from 'mobx';
import { observer } from 'mobx-react';
import Msdyn365, { isUserTypeB2B } from '@msdyn365-commerce/core';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { IOrderDetailsData } from './order-details.data';
import { IOrderDetailsProps } from './order-details.props.autogenerated';
import { IOrderDetailResponse } from '../../actions/get-order-details.action';
import { Toaster, toast } from 'sonner';
import { ERROR_CODE_SERVER_ERROR_INDEX } from '../../configuration';
import { ModalProvider } from '../../components/modal';

export interface IOrderDetailsViewProps extends IOrderDetailsProps<IOrderDetailsData> {
    moduleProps: IModuleProps;
    footerProps?: INodeProps;
    loading?: React.ReactNode;
    heading?: React.ReactNode;
    placedBy?: React.ReactNode;
    orderDetails?: IOrderDetailResponse;
    props?: any;
}

/**
 * Interface for IOrderDetailsState.
 */
export interface IOrderDetailsState {
    isModalOpen: boolean;
}

/**
 *
 * OrderDetails component.
 * @extends {React.PureComponent<IOrderDetailsProps<IOrderDetailsData>>}
 */
@observer
class OrderDetails extends React.PureComponent<IOrderDetailsProps<IOrderDetailsData>> {
    @computed
    get orderDetails(): IOrderDetailResponse {
        const rawData = this.props.data.getOrderDetails.result;
        if (rawData?.error) {
            if (rawData.error.errorCode && Number(String(rawData.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
                this._showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable);
            }
            return {} as IOrderDetailResponse;
        }
        return toJS(rawData) as IOrderDetailResponse;
    }

    @computed get order(): SalesOrder | undefined {
        return get(this.props, 'data.orderHydration.result.salesOrder');
    }

    public constructor(props: IOrderDetailsProps<IOrderDetailsData>) {
        super(props);
    }

    private _showToast = (heading: string, description: string) => {
        toast.error(
            <div className='toast' role='alert'>
                <div className='toast-header'>
                    <h4>{heading}</h4>
                    <button className='toast-close' onClick={() => toast.dismiss()}></button>
                </div>
                <div className='toast-body'>
                    <span>{description}</span>
                </div>
            </div>
        );
    };

    public render(): JSX.Element {
        const {
            resources: {}
        } = this.props;
        const { salesId = '', transactionId = '' } = this.props.context.actionContext.requestContext.query ?? {};

        let viewProps: IOrderDetailsViewProps = this.getOrderDetailsProps();
        if (isUserTypeB2B(this.props.context.request)) {
            viewProps = {
                ...viewProps
            };
        }

        if ((salesId || transactionId) && !this.props.context.actionContext.requestContext.user.isAuthenticated) {
            const signInUrl: string = this.props.context.actionContext.requestContext.user.signInUrl ?? '';
            if (Msdyn365.isBrowser) {
                window.location.assign(signInUrl);
            }
        }

        viewProps = {
            ...viewProps
        };

        return (
            <>
                <Toaster
                    richColors
                    position='top-right'
                    toastOptions={{
                        classNames: {
                            default: 'toast-light',
                            error: 'toast-danger',
                            success: 'toast-success',
                            warning: 'toast-warning',
                            info: 'toast-info'
                        }
                    }}
                />
                <ModalProvider props={this.props}>{this.props.renderView(viewProps) as React.ReactElement}</ModalProvider>
            </>
        );
    }

    /**
     * Get the order details view properties.
     * @returns The initial values of order details view properties.
     */
    private readonly getOrderDetailsProps = (): IOrderDetailsViewProps => {
        const moduleProps = { moduleProps: this.props, className: classname('ms-order-details', this.props.config.className) };

        return {
            ...this.props,
            moduleProps,
            footerProps: { className: 'ms-order-details__footer' },
            orderDetails: this.orderDetails,
            props: this.props
        };
    };
}

export default OrderDetails;
